import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Eraser, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/release`;
const err = (e) => e.response?.data?.detail || 'Errore di rete: riprova';

function Pad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  useEffect(() => {
    const c = ref.current; const r = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * r; c.height = c.offsetHeight * r;
    const x = c.getContext('2d'); x.scale(r, r); x.lineWidth = 2.2; x.lineCap = 'round'; x.strokeStyle = '#0B1F4D';
  }, []);
  const pos = (e) => { const b = ref.current.getBoundingClientRect(); return [e.clientX - b.left, e.clientY - b.top]; };
  const down = (e) => { drawing.current = true; ref.current.setPointerCapture(e.pointerId); const x = ref.current.getContext('2d'); x.beginPath(); x.moveTo(...pos(e)); };
  const move = (e) => { if (!drawing.current) return; const x = ref.current.getContext('2d'); x.lineTo(...pos(e)); x.stroke(); };
  const up = () => { if (drawing.current) { drawing.current = false; onChange(ref.current.toDataURL('image/png')); } };
  const clear = () => { const c = ref.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); onChange(null); };
  return (
    <div>
      <canvas ref={ref} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} className="w-full h-40 bg-white border border-[#C9C2B4] rounded touch-none" data-testid="sign-pad" />
      <button type="button" onClick={clear} className="mt-1 text-xs text-[#555] inline-flex items-center gap-1" data-testid="sign-clear"><Eraser size={12} />Cancella firma</button>
    </div>
  );
}

export default function SignReleasePage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [sig, setSig] = useState(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  useEffect(() => { axios.get(`${API}/${token}`).then((r) => setD(r.data)).catch((e) => setError(err(e))); }, [token]);
  const otp = async () => { setBusy('otp'); setMsg(''); try { await axios.post(`${API}/${token}/otp`); setSent(true); } catch (e) { setMsg(err(e)); } finally { setBusy(''); } };
  const sign = async () => { setBusy('sign'); setMsg(''); try { await axios.post(`${API}/${token}/sign`, { code, signature: sig, accept: ok }); setD({ ...d, status: 'firmata' }); } catch (e) { setMsg(err(e)); } finally { setBusy(''); } };
  const btn = 'h-11 px-5 rounded bg-[#0B1F4D] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40';
  return (
    <div className="min-h-screen bg-[#F6F3EE] text-[#1A1A1A] py-8 px-4" data-testid="sign-release-page">
      <div className="max-w-2xl mx-auto bg-white border border-[#E4DED3] rounded-lg p-6 sm:p-10">
        {error ? <p className="text-sm text-[#B42318]" data-testid="sign-error">{error}</p> : !d ? <Loader2 className="animate-spin mx-auto" /> : d.status === 'firmata' ? (
          <div className="text-center py-10" data-testid="sign-done"><CheckCircle2 size={44} className="text-[#2E9E6A] mx-auto mb-3" /><p className="text-lg font-semibold mb-1">Dichiarazione firmata</p>
            <p className="text-sm text-[#555]">Grazie. Riceverai via email la copia firmata; {d.company} l'ha già ricevuta.</p></div>
        ) : (
          <>
            <p className="text-xs uppercase tracking-[0.2em] text-[#8A6D1D] mb-2">{d.company}</p>
            <h1 className="text-xl font-semibold mb-5">{d.title}</h1>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#8A6D1D] mb-2">Parte 1 · Premesse, consenso e manleva</p>
            <div className="space-y-3 text-sm leading-relaxed mb-6" data-testid="sign-text">{d.paragraphs.map((p, i) => <p key={i} dangerouslySetInnerHTML={{ __html: p }} />)}</div>
            {d.delega && (
              <div className="border-t border-[#E5E0D5] pt-5 mb-6" data-testid="sign-delega">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#8A6D1D] mb-1">Parte 2 · {d.delega_title}</p>
                <p className="text-xs text-[#6B6B6B] mb-3">Documento separato: è l'unico che Smart Info invia agli enti, senza premesse e senza il nome del nuovo datore di lavoro.</p>
                <div className="space-y-3 text-sm leading-relaxed">{d.delega.map((p, i) => <p key={i} dangerouslySetInnerHTML={{ __html: p }} />)}</div>
              </div>
            )}
            <a href={`${API}/${token}/privacy.pdf`} target="_blank" rel="noreferrer" className="inline-block text-sm text-[#0B1F4D] underline mb-5" data-testid="sign-privacy-link">Leggi l'informativa sul trattamento dei dati personali (PDF)</a>
            <label className="flex items-start gap-2 text-sm mb-5"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-1" data-testid="sign-accept" />Ho letto la dichiarazione (Parte 1) e la delega (Parte 2) e le sottoscrivo.</label>
            <p className="text-sm font-semibold mb-2">La tua firma</p>
            <Pad onChange={setSig} />
            <div className="mt-6 space-y-3">
              {!sent ? <button onClick={otp} disabled={!!busy} className={btn} data-testid="sign-otp-btn">{busy === 'otp' && <Loader2 size={14} className="animate-spin" />}Ricevi il codice di conferma via email</button> : (
                <>
                  <p className="text-sm text-[#555]">Abbiamo inviato un codice a {d.email}. <button onClick={otp} className="underline" data-testid="sign-otp-resend">Invia di nuovo</button></p>
                  <input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="Codice di 6 cifre" className="w-full sm:w-60 h-11 border border-[#C9C2B4] rounded px-3 tracking-[0.3em]" data-testid="sign-code" />
                  <div><button onClick={sign} disabled={!!busy || code.length !== 6 || !sig || !ok} className={btn} data-testid="sign-submit">{busy === 'sign' && <Loader2 size={14} className="animate-spin" />}Firma la dichiarazione</button></div>
                </>
              )}
              {msg && <p className="text-sm text-[#B42318]" data-testid="sign-msg">{msg}</p>}
            </div>
            <p className="text-[11px] text-[#777] mt-8">Firma elettronica raccolta da BOND · Smart Info: registriamo data, ora, indirizzo IP e dispositivo a garanzia dell'autenticità.</p>
          </>
        )}
      </div>
    </div>
  );
}
